// boardGridModel.js — GATE-VERIFIED odds-matrix data core (games × books, best price flagged).
// IDENTICAL to abet-scraper-scripts/lib/board_grid_model.mjs (trusted synthesis of swarm Round D, 17/17 on
// tests/test_board_grid_model_acceptance_v1.mjs — real live-board fixture). Do NOT edit here without
// re-running that gate; keep the two copies byte-equivalent below this header.
// v1.1 semantics: doubleheader time-cluster grouping (>90min apart = separate games; minutes-level book
// time-skew never splits), is_primary-preferred cells, modal consensus line, best = max american at the
// consensus line (ties all flagged), one-sided markets render their side, prices VERBATIM.

// v1.2 — TIME-CLUSTERING REMOVED (incident 2026-07-21). It fragmented real games and was the cause of the
// "only one book has a moneyline" disaster on the live board. Books DISAGREE on start time by HOURS:
// measured on CIN @ SEA, kalshi + mybookie reported 3:40 PM ET while 27 other books reported 9:40 PM ET
// (a 6-hour timezone defect in those two scrapers). A 90-minute cluster split that one game into two grid
// rows — 28 books in one, 2 in the other — and TB @ TOR into THREE rows (22 / 1 / 1). Book-reported
// start time can NEVER be part of the grouping key. Games group by (league, event, et_date) — the board's
// own join key. Doubleheaders need a real per-game identifier from the engine, not a time heuristic.
// FULL-GAME PERIOD (incident 2026-07-21b): the engine does NOT emit a uniform period. MLB rows carry
// period="Game"; NFL/CFB rows carry period=null (measured: 524 NFL moneylines across 14 books, ALL null).
// Requiring period === "Game" silently discarded every NFL/CFB line and produced the false
// "no NFL moneylines" reading. Full game = "Game" OR null/empty. Real sub-periods ("First 5 Innings",
// "1st Inning", "8th Inning", halves/quarters) still filter out correctly.
// EXPORTED (2026-08-16): this rule was module-private, so OddsMatrixView could not
// reuse it and carried its own `period !== 'Game'` copy. 7547f00 fixed the rule HERE
// and touched only this file, so the copy in availabilityByLeague kept the bug for
// another four weeks. One definition, imported everywhere, is the actual fix.
export function isFullGame(period) {
  return period === null || period === undefined || period === '' || period === 'Game';
}

export function buildGrid(rows, opts) {
  const wantFullGame = isFullGame(opts.period);
  const filtered = rows.filter((r) => {
    if (r.american == null) return false;
    if (opts.league !== null && r.league !== opts.league) return false;
    if (r.bet_type !== opts.bet_type) return false;
    return wantFullGame ? isFullGame(r.period) : r.period === opts.period;
  });

  const books = Array.from(new Set(filtered.map((r) => r.book))).sort();

  const buckets = {};
  for (const r of filtered) {
    const key = `${r.league}|${r.event}|${r.et_date}`;
    (buckets[key] = buckets[key] || []).push(r);
  }
  const groups = Object.values(buckets).map((rows) => ({ rows, game_index: 0 }));

  const games = groups.map(({ rows: g, game_index }) => {
    const { league, event, et_date, bet_type } = g[0];
    const [away, home] = event.split(" @ ");

    const modal = (vals) => {
      const freq = new Map();
      let best = null, n = 0;
      for (const v of vals) {
        if (v === null || v === undefined) continue;
        const c = (freq.get(v) || 0) + 1;
        freq.set(v, c);
        if (c > n || (c === n && (best === null || v < best))) { n = c; best = v; }
      }
      return best;
    };
    const et_time = modal(g.map((r) => r.et_time)) ?? "";
    const consensus_line = modal(g.map((r) => r.line));

    const sides = bet_type === "Total" ? ["Over", "Under"] : [away, home];

    const cells = {};
    for (const r of g) {
      const cur = (cells[r.book] = cells[r.book] || {});
      const cell = { american: r.american, line: r.line, state: r.state, is_primary: r.is_primary, best: false };
      if (!cur[r.selection] || (r.is_primary === true && cur[r.selection].is_primary !== true)) {
        cur[r.selection] = cell;
      }
    }

    for (const sel of sides) {
      let bestAme = null;
      for (const b of books) {
        const cell = cells[b] && cells[b][sel];
        if (!cell) continue;
        if (consensus_line !== null && cell.line !== consensus_line) continue;
        if (bestAme === null || cell.american > bestAme) bestAme = cell.american;
      }
      if (bestAme === null) continue;
      for (const b of books) {
        const cell = cells[b] && cells[b][sel];
        if (cell && (consensus_line === null || cell.line === consensus_line) && cell.american === bestAme) {
          cell.best = true;
        }
      }
    }

    return { league, event, et_date, et_time, away, home, sides, consensus_line, cells, game_index };
  });

  games.sort((a, b) =>
    a.et_date !== b.et_date ? a.et_date.localeCompare(b.et_date) : a.event.localeCompare(b.event));

  let cells = 0;
  for (const g of games) for (const b of books) if (g.cells[b]) cells += Object.keys(g.cells[b]).length;

  return { books, games, counts: { games: games.length, books: books.length, cells } };
}

// ---- dashboard adapters (not gate-scoped) ----

// Spread flavor differs by league (MLB "Run Line", NHL "Puck Line", basketball/football "Spread").
export const SPREAD_BET_TYPES = ["Spread", "Run Line", "Puck Line"];

// Run buildGrid over several bet_type variants and merge (a league only ever uses one flavor,
// so per-variant semantics — consensus line, best flags — stay intact).
export function buildGridMulti(rows, league, betTypes, period = "Game") {
  const parts = betTypes.map((bt) => buildGrid(rows, { league, bet_type: bt, period }));
  const books = Array.from(new Set(parts.flatMap((p) => p.books))).sort();
  const games = parts.flatMap((p, i) => p.games.map((g) => ({ ...g, bet_type: betTypes[i] })));
  games.sort((a, b) =>
    a.et_date !== b.et_date ? a.et_date.localeCompare(b.et_date)
    : a.league !== b.league ? a.league.localeCompare(b.league)
    : a.event.localeCompare(b.event));
  const cells = parts.reduce((n, p) => n + p.counts.cells, 0);
  return { books, games, counts: { games: games.length, books: books.length, cells } };
}
